import React, { FC, memo, useEffect, useState, useMemo } from 'react'

import { IProduct } from '../../types/products'
import { IHash } from '../../types/common'
import Input from '../../components/Input'
import Button from '../../components/Button'
import Search from '../../components/Search'
import ThresholdIcon from '../../icons/threshold.svg'
import ToggleButtonGroup from '../../components/ToggleButtonGroup'
import {
  ILocationItem,
  LocationTypes,
  LOCATION_TYPE_PLURAL_LABELS,
} from '../../types/location'
import TypeBadge from '../../components/TypeBadge'
import EditAmountModal from './EditAmountModal'

const THRESHOLD_ICON_COLORS = {
  active: '#E4922F',
  inactive: '#757D9F',
}

type LocationTypeFilter = 'all' | 'truck' | 'bin'

interface ILocationsFormProps {
  product: IProduct
  loading: boolean
  errors: IHash<string>
  truckInventoryFeature: boolean
  binInventoryFeature: boolean
  onSave: (product: IProduct) => void
  onRemove: (product: IProduct) => void
  onCancel: () => void
}

interface ILocationItemThreshold {
  id: number
  threshold: number | null
}

const formatThreshold = (value: number | null | undefined): string => {
  return !value && value !== 0 ? '-' : String(value)
}

const parseThreshold = (value: string): number | null => {
  const parsed = parseInt(value, 10)
  return parsed >= 0 ? parsed : null
}

const parseGlobalThreshold = (value: string): number | null => {
  const parsed = parseInt(value, 10)
  return parsed ? parsed : null
}

const LocationsForm: FC<ILocationsFormProps> = ({
  product: initialProduct,
  loading,
  errors,
  truckInventoryFeature,
  binInventoryFeature,
  onSave,
  onRemove,
  onCancel,
}) => {
  const bothEnabled = truckInventoryFeature && binInventoryFeature
  const hasLocationFeature = truckInventoryFeature || binInventoryFeature

  const [editAmountItem, setEditAmountItem] = useState<ILocationItem | null>(
    null,
  )
  const [product, setProduct] = useState<IProduct>({ ...initialProduct })
  const [activeType, setActiveType] = useState<LocationTypeFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')

  const [truckGlobalThreshold, setTruckGlobalThreshold] = useState<string>(
    formatThreshold(initialProduct.truckLocationThreshold),
  )
  const [binGlobalThreshold, setBinGlobalThreshold] = useState<string>(
    formatThreshold(initialProduct.binLocationThreshold),
  )
  const [technicianGlobalThreshold, setTechnicianGlobalThreshold] =
    useState<string>(formatThreshold(initialProduct.technicianThreshold))
  const [locationItemsThresholds, setLocationItemsThresholds] = useState<
    ILocationItemThreshold[]
  >([])

  useEffect(() => {
    setProduct({ ...initialProduct })
    setTruckGlobalThreshold(
      formatThreshold(initialProduct.truckLocationThreshold),
    )
    setBinGlobalThreshold(formatThreshold(initialProduct.binLocationThreshold))
    setTechnicianGlobalThreshold(
      formatThreshold(initialProduct.technicianThreshold),
    )
    if (initialProduct.locationItems) {
      setLocationItemsThresholds(
        initialProduct.locationItems.map((item) => ({
          id: item.id,
          threshold: item.threshold ?? null,
        })),
      )
    }
  }, [initialProduct])

  const handleThresholdChange = (value: string) => {
    const filtered = value.replace(/[^0-9]/g, '')
    if (!filtered && filtered !== '0') return '-'
    return filtered
  }

  const handleGlobalThresholdChange = (value: string) => {
    const filtered = value.replace(/[^0-9]/g, '')
    if (!filtered) return '-'
    return filtered
  }

  const handleLocationThresholdChange = (itemId: number, value: string) => {
    const displayValue = handleThresholdChange(value)
    setLocationItemsThresholds((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, threshold: parseThreshold(displayValue) }
          : item,
      ),
    )
  }

  const getLocationThreshold = (itemId: number): string => {
    const item = locationItemsThresholds.find((i) => i.id === itemId)
    return formatThreshold(item?.threshold)
  }

  const handleEditAmount = (amount: number, reason: string) => {
    if (!editAmountItem) return
    onSave({
      ...product,
      locationItemsAttributes: [
        { id: editAmountItem.id, inventory: amount, reason },
      ],
    } as any)
    setEditAmountItem(null)
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    onSave({
      ...product,
      truckLocationThreshold: parseGlobalThreshold(truckGlobalThreshold),
      binLocationThreshold: parseGlobalThreshold(binGlobalThreshold),
      technicianThreshold: parseGlobalThreshold(technicianGlobalThreshold),
      locationItemsAttributes: locationItemsThresholds.map((item) => ({
        id: item.id,
        threshold: item.threshold,
      })),
    } as any)
  }

  const filteredLocationItems = useMemo(() => {
    const all = product.locationItems || []
    const byType =
      bothEnabled && activeType !== 'all'
        ? all.filter((item) => item.location?.locationType === activeType)
        : all

    if (!searchTerm.trim()) return byType
    const lower = searchTerm.toLowerCase()
    return byType.filter((item) => {
      const code = item.location?.code?.toLowerCase() || ''
      const userName = item.location?.user?.name?.toLowerCase() || ''
      return code.includes(lower) || userName.includes(lower)
    })
  }, [product.locationItems, activeType, searchTerm, bothEnabled])

  const hasStockItems = filteredLocationItems.filter(
    (item) => (item.inventory ?? 0) > 0,
  )
  const outOfStockItems = filteredLocationItems.filter(
    (item) => (item.inventory ?? 0) <= 0,
  )

  const renderLocationItem = (item: ILocationItem) => {
    const locationName = item.location?.code || `Location ${item.locationId}`
    const displayName = item.location?.description
      ? `${locationName} - ${item.location.description}`
      : locationName
    const thresholdValue = getLocationThreshold(item.id)
    const itemThreshold = locationItemsThresholds.find(
      (i) => i.id === item.id,
    )?.threshold
    const thresholdIconColor = itemThreshold
      ? THRESHOLD_ICON_COLORS.active
      : THRESHOLD_ICON_COLORS.inactive
    const isOutOfStock = (item.inventory ?? 0) <= 0

    const locationType = item.location?.locationType

    return (
      <div key={item.id} className="product-info_locations-item">
        {locationType && <TypeBadge locationType={locationType} />}
        <div className="product-info_locations-name">{displayName}</div>
        <div className="product-info_locations-inventory">
          {locationType === LocationTypes.BIN ? (
            <button
              type="button"
              className="product-info_locations-inventory-edit"
              onClick={() => setEditAmountItem(item)}
            >
              {item.inventory ? item.inventory : '–'}
            </button>
          ) : isOutOfStock ? (
            '–'
          ) : (
            (item.inventory ?? 0)
          )}
        </div>
        {locationType !== LocationTypes.BIN && (
          <div className="product-info_locations-threshold">
            <ThresholdIcon style={{ color: thresholdIconColor }} />
            <Input
              value={thresholdValue}
              onChange={(e) =>
                handleLocationThresholdChange(item.id, e.target.value)
              }
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="product-info_locations">
        {errors.inventory && (
          <div className="product-info_locations-error">{errors.inventory}</div>
        )}
        <div className="product-info_locations-section">
          <div className="product-info_label">LOCATION THRESHOLDS</div>
        </div>

        <div className="product-info_locations-thresholds">
          <div className="product-info_locations-row">
            <div className="product-info_locations-row-label">
              Set maximum for all Technicians
            </div>
            <div className="product-info_locations-threshold">
              <ThresholdIcon
                style={{
                  color: parseThreshold(technicianGlobalThreshold)
                    ? THRESHOLD_ICON_COLORS.active
                    : THRESHOLD_ICON_COLORS.inactive,
                }}
              />
              <Input
                value={technicianGlobalThreshold}
                onChange={(e) =>
                  setTechnicianGlobalThreshold(
                    handleGlobalThresholdChange(e.target.value),
                  )
                }
                error={errors.technicianThreshold}
              />
            </div>
          </div>
          {truckInventoryFeature && (!bothEnabled || activeType !== 'bin') && (
            <div className="product-info_locations-row">
              <div className="product-info_locations-row-label">
                Set minimum for all trucks
              </div>
              <div className="product-info_locations-threshold">
                <ThresholdIcon
                  style={{
                    color: parseThreshold(truckGlobalThreshold)
                      ? THRESHOLD_ICON_COLORS.active
                      : THRESHOLD_ICON_COLORS.inactive,
                  }}
                />
                <Input
                  value={truckGlobalThreshold}
                  onChange={(e) =>
                    setTruckGlobalThreshold(
                      handleGlobalThresholdChange(e.target.value),
                    )
                  }
                  error={errors.truckLocationThreshold}
                />
              </div>
            </div>
          )}
        </div>

        {bothEnabled && (
          <ToggleButtonGroup<LocationTypeFilter>
            options={['truck', 'bin', 'all']}
            value={activeType}
            renderOption={(t) => LOCATION_TYPE_PLURAL_LABELS[t] ?? 'All'}
            onChange={setActiveType}
          />
        )}

        {hasLocationFeature && (
          <>
            <div className="product-info_locations-controls">
              <Search
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by code or user name"
              />
            </div>

            <div className="product-info_locations-list">
              {hasStockItems.map(renderLocationItem)}
            </div>

            {outOfStockItems.length > 0 && (
              <>
                <div className="product-info_locations-section">
                  <div className="product-info_label">OUT OF STOCK</div>
                </div>

                <div className="product-info_locations-list">
                  {outOfStockItems.map(renderLocationItem)}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="product-info_section -footer">
        <Button
          color="success"
          type="submit"
          disabled={loading}
          loading={loading}
        >
          Save
        </Button>
        <div className="product-info_cancel" onClick={onCancel}>
          Cancel
        </div>
        <div
          className="product-info_archive"
          onClick={() => !loading && onRemove(product)}
        >
          Remove Item
        </div>
      </div>

      <EditAmountModal
        visible={editAmountItem !== null}
        currentAmount={editAmountItem?.inventory ?? 0}
        locationCode={editAmountItem?.location?.code}
        onSave={handleEditAmount}
        onClose={() => setEditAmountItem(null)}
      />
    </form>
  )
}

export default memo(LocationsForm)
